import {
  calculateTransactionTax,
  transactionTaxName,
} from '@/components/deals/appraisal/appraisal-transaction-tax'
import {
  isCommercialUseType,
  isCoWorkStrategy,
} from '@/components/deals/appraisal/appraisal-data'
import {
  normalizeTargetMetric,
  targetMetricLabel,
  targetReturnError,
} from '@/components/deals/appraisal/appraisal-targets'

export const APPRAISAL_FORMULA_VERSION = 'appraisal_exit_mode_v2'

const FORMULA_KEYS = {
  profit_on_gdc: 'residual_profit_on_gdc',
  profit_on_gdv: 'residual_profit_on_gdv',
  roce: 'residual_roce',
}

export function parseNumber(value) {
  return Number(String(value ?? '').replace(/[,£%\s]/g, '')) || 0
}

export function formatNumber(value) {
  return Number(value || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })
}

export function formatCurrency(value) {
  return `£${formatNumber(value)}`
}

export function formatPercent(value) {
  return `${Number(value || 0).toLocaleString('en-GB', { maximumFractionDigits: 1 })}%`
}

export function strategyFromConcept(selectedStrategy) {
  const name = String(selectedStrategy?.name ?? '').toLowerCase()
  if (name.includes('hmo')) return 'HMO'
  if (name.includes('commercial conversion')) return 'Commercial Conversion'
  if (isCoWorkStrategy(selectedStrategy)) return 'Commercial / Mixed-Use'
  if (name.includes('commercial') || name.includes('mixed')) return 'Commercial / Mixed-Use'
  return 'BRRR / Flip'
}

export function calculateHmoSummary(rooms, opex, yieldPercent) {
  const grossMonthlyRent = rooms.reduce((sum, room) => sum + parseNumber(room.adoptedRent), 0)
  const grossAnnualRent = grossMonthlyRent * 12
  const netOperatingIncome = grossAnnualRent * (1 - (parseNumber(opex) / 100 || 0))
  const parsedYield = parseNumber(yieldPercent)
  const commercialValue = parsedYield > 0 ? netOperatingIncome / (parsedYield / 100) : 0

  return { grossMonthlyRent, grossAnnualRent, netOperatingIncome, commercialValue }
}

export function calculateCommercialSummary(units) {
  const summary = summarizeCommercialUnits(units)

  return {
    commercial: summary.commercial,
    residential: summary.residential,
    income: summary.income,
  }
}

export function calculateCommercialUnit(unit) {
  const isCommercialUse = isCommercialUseType(unit.useType)
  const size = parseNumber(unit.sizeSqft)
  const opex = parseNumber(unit.opexPercent)
  const yieldPercent = parseNumber(unit.adoptedYield)
  const adoptedSqftYear = parseNumber(unit.adoptedSqftYear)
  const adoptedMonthlyRent = parseNumber(unit.adoptedMonthlyRent)
  const adoptedAdr = parseNumber(unit.adoptedAdr)
  const occupancy = parseNumber(unit.occupancy)
  const adoptedValue = parseNumber(unit.adoptedValue)
  const adoptedSqftValue = parseNumber(unit.adoptedSqftValue)
  const derivedAnnualRent = size * adoptedSqftYear
  const commercialAnnualRent = adoptedSqftYear > 0 ? derivedAnnualRent : adoptedMonthlyRent * 12
  const astAnnualRent = adoptedMonthlyRent * 12
  const netMonthlyIncome = adoptedAdr * 30 * (occupancy / 100) * (1 - opex / 100)
  const netAnnualIncome = netMonthlyIncome * 12
  const incomeForCommercialValuation = isCommercialUse
    ? commercialAnnualRent
    : unit.operatingModel === 'SA'
      ? netAnnualIncome
      : astAnnualRent
  const netOperatingIncome = unit.operatingModel === 'SA' && !isCommercialUse
    ? netAnnualIncome
    : incomeForCommercialValuation * (1 - opex / 100)
  const grossAnnualIncome = isCommercialUse
    ? commercialAnnualRent
    : unit.operatingModel === 'SA'
      ? adoptedAdr * 30 * (occupancy / 100) * 12
      : astAnnualRent
  const operatingExpenses = Math.max(grossAnnualIncome - netOperatingIncome, 0)
  const commercialValue = yieldPercent > 0 ? netOperatingIncome / (yieldPercent / 100) : 0

  if (isCommercialUse) {
    const fallbackValue = unit.valuationBasis === '£/sqft'
      ? size * adoptedSqftValue
      : adoptedValue
    const value = adoptedSqftYear > 0 && yieldPercent > 0
      ? commercialValue
      : fallbackValue

    return {
      adoptedIncome: commercialAnnualRent,
      derivedAnnualRent,
      netMonthlyIncome: netOperatingIncome / 12,
      netAnnualIncome: netOperatingIncome,
      netOperatingIncome,
      grossAnnualIncome,
      operatingExpenses,
      value,
    }
  }

  const adoptedIncome = unit.operatingModel === 'SA' ? netAnnualIncome : astAnnualRent
  const value = unit.valuationBasis === 'Commercial'
    ? commercialValue
    : unit.valuationBasis === '£/sqft'
      ? size * adoptedSqftValue
      : adoptedValue

  return {
    adoptedIncome,
    derivedAnnualRent: 0,
    netMonthlyIncome: netOperatingIncome / 12,
    netAnnualIncome: netOperatingIncome,
    netOperatingIncome,
    grossAnnualIncome,
    operatingExpenses,
    value,
  }
}

export function buildDefaultResidualAppraisalResult() {
  return buildResidualAppraisalResult(mockAppraisalInputs())
}

export function buildResidualAppraisalResult(inputs) {
  const assumptions = appraisalAssumptions(inputs)
  const requestedMetric = inputs?.strategy?.targetMetric
  const metric = normalizeTargetMetric(requestedMetric)

  if (!metric) return invalidMetricResult(requestedMetric, assumptions)

  const solved = metric === 'roce'
    ? solveRocePurchasePrice(assumptions)
    : solveProfitPurchasePrice(metric, assumptions)
  const maxPurchasePrice = Math.max(0, Math.round(solved.price))
  const purchaseCosts = purchaseCostsAtPrice(maxPurchasePrice, assumptions)
  const gdc = maxPurchasePrice + purchaseCosts.total + assumptions.projectCosts
  const cashLeft = gdc - assumptions.netRefinanceAdvance
  const annualCashflow = assumptions.netOperatingIncome - assumptions.refinanceInterest
  const achievedRoce = cashLeft > 0 ? (annualCashflow / cashLeft) * 100 : 0
  const profit = assumptions.gdv - gdc
  const profitOnGdv = assumptions.gdv > 0 ? (profit / assumptions.gdv) * 100 : 0
  const profitOnGdc = gdc > 0 ? (profit / gdc) * 100 : 0
  const achievedReturns = { roce: achievedRoce, profit_on_gdv: profitOnGdv, profit_on_gdc: profitOnGdc }
  const metricLabel = targetMetricLabel(metric)
  const sensitivitySignals = [
    { label: 'GDV / post works value', value: formatCurrency(assumptions.gdv) },
    { label: 'Gross development cost', value: formatCurrency(gdc) },
    { label: 'Profit on GDV', value: formatPercent(profitOnGdv) },
    { label: 'Profit on GDC', value: formatPercent(profitOnGdc) },
    { label: 'Gross annual income', value: formatCurrency(assumptions.grossAnnualIncome) },
    { label: 'Annual operating expenses', value: formatCurrency(assumptions.operatingExpenses) },
    { label: 'Net operating income', value: formatCurrency(assumptions.netOperatingIncome) },
    { label: 'Annual refinance interest', value: formatCurrency(assumptions.refinanceInterest) },
    { label: 'Annual net cashflow', value: formatCurrency(annualCashflow) },
    { label: 'Cash left in', value: formatCurrency(cashLeft) },
  ]
  if (metric !== 'roce' && cashLeft > 0 && assumptions.netOperatingIncome > 0 &&
      assumptions.refinanceAdvance > 0) {
    sensitivitySignals.push({ label: 'ROCE', value: formatPercent(achievedRoce) })
  }

  return {
    maxPrice: formatCurrency(maxPurchasePrice),
    targetClose: formatCurrency(Math.round(maxPurchasePrice * 0.95)),
    initialOffer: formatCurrency(Math.round(maxPurchasePrice * 0.9)),
    roce: formatPercent(achievedRoce),
    cashflow: `${formatCurrency(annualCashflow / 12)}/mo`,
    profit: formatCurrency(profit),
    formulaKey: FORMULA_KEYS[metric],
    formulaVersion: APPRAISAL_FORMULA_VERSION,
    metric,
    metricLabel,
    targetReturn: formatPercent(assumptions.targetReturn),
    achievedTarget: formatPercent(achievedReturns[metric]),
    maxPurchasePrice,
    costBreakdown: [
      { label: assumptions.transactionTaxName, value: formatCurrency(purchaseCosts.transactionTax) },
      { label: 'Purchase finance', value: formatCurrency(purchaseCosts.purchaseFinance) },
      { label: 'Purchase fees', value: formatCurrency(purchaseCosts.fixed) },
      { label: 'Project costs', value: formatCurrency(assumptions.projectCosts) },
      { label: 'Refinance fees', value: formatCurrency(assumptions.refinanceFees) },
    ],
    sensitivitySignals,
    interpretation: solved.notes || calculationNote(metric, assumptions.targetReturn),
    raw: {
      gdv: assumptions.gdv,
      grossAnnualIncome: assumptions.grossAnnualIncome,
      operatingExpenses: assumptions.operatingExpenses,
      netOperatingIncome: assumptions.netOperatingIncome,
      refinanceAdvance: assumptions.refinanceAdvance,
      refinanceInterest: assumptions.refinanceInterest,
      projectCosts: assumptions.projectCosts,
      disposalCosts: assumptions.disposalCosts,
      purchaseCosts,
      annualCashflow,
      cashLeft,
      achievedRoce,
      equityProfit: profit,
      gdc,
      profit,
      profitOnGdc,
      profitOnGdv,
    },
  }
}

export function calculateSdlt(price, propertyType = 'non-residential') {
  return calculateTransactionTax(price, {
    jurisdiction: 'england_northern_ireland',
    propertyType: propertyType === 'residential' ? 'residential' : 'non_residential',
  })
}

function appraisalAssumptions(inputs) {
  const strategy = inputs?.strategy?.currentStrategy ?? 'BRRR / Flip'
  const scheme = inputs?.scheme ?? {}
  const costs = inputs?.costs ?? {}
  const finance = inputs?.finance ?? {}
  const holding = inputs?.holding ?? {}
  const valuation = valuationForStrategy(strategy, scheme)
  const baseCosts = costs.baseCostValues ?? {}
  const refurbCost = parseNumber(baseCosts['Refurb / Build Cost'])
  const contingency = refurbCost * (parseNumber(baseCosts['Contingency (% of refurb/build)']) / 100)
  const professionalFees = parseNumber(baseCosts['Professional Fees'])
  const developmentCostBase = refurbCost + contingency + professionalFees
  const purchaseFees = parseNumber(baseCosts['Legal Costs']) + parseNumber(baseCosts['Survey Costs'])
  const conditionalCosts = enabledConditionalCosts(costs)
  const disposalCosts = disposalCostsForExitMode(costs, scheme, valuation.gdv)
  const monthsToRefinance = parseNumber(holding.monthsToRefinance)
  const holdingCosts = parseNumber(holding.monthlyHoldingCosts) * monthsToRefinance
  const projectCosts =
    refurbCost +
    contingency +
    professionalFees +
    conditionalCosts +
    disposalCosts +
    holdingCosts +
    developmentFinanceCost(finance, developmentCostBase, monthsToRefinance)
  const refinanceAdvance = valuation.gdv * (parseNumber(finance.refinanceLtvPercent) / 100)
  const refinanceInterest = refinanceAdvance * (parseNumber(finance.refinanceInterestRatePa) / 100)
  const refinanceArrangementFee = refinanceAdvance *
    (refinanceArrangementFeePercent(finance) / 100)
  const refinanceFees = parseNumber(finance.refinanceBrokerFee) +
    refinanceArrangementFee +
    parseNumber(finance.refinanceValuationFee)
  const transactionTax = inputs?.transactionTax ?? {}
  const jurisdiction = transactionTax.jurisdiction ?? 'england_northern_ireland'

  return {
    strategy,
    gdv: valuation.gdv,
    grossAnnualIncome: valuation.grossAnnualIncome,
    operatingExpenses: valuation.operatingExpenses,
    netOperatingIncome: valuation.netOperatingIncome,
    targetReturn: parseNumber(inputs?.strategy?.targetReturn),
    projectCosts,
    disposalCosts,
    purchaseFees,
    finance,
    monthsToRefinance,
    refinanceAdvance,
    refinanceInterest,
    refinanceFees,
    netRefinanceAdvance: refinanceAdvance - refinanceFees,
    transactionTax: {
      jurisdiction,
      propertyType: transactionTax.propertyType ?? sdltTypeForStrategy(strategy, scheme),
      additionalResidentialProperty: Boolean(transactionTax.additionalResidentialProperty),
    },
    transactionTaxName: transactionTaxName(jurisdiction),
  }
}

function solveRocePurchasePrice(assumptions) {
  if (assumptions.gdv <= 0 || assumptions.netOperatingIncome <= 0) {
    return { price: 0, notes: 'ROCE solve requires GDV and income assumptions.' }
  }

  const annualCashflow = assumptions.netOperatingIncome - assumptions.refinanceInterest
  const validationError = targetReturnError('roce', assumptions.targetReturn)
  const target = assumptions.targetReturn / 100
  if (validationError) return { price: 0, notes: validationError }
  if (annualCashflow <= 0) {
    return { price: 0, notes: 'ROCE target cannot be solved because post-finance cashflow is not positive.' }
  }

  const requiredCashLeft = annualCashflow / target
  const residual = (price) => {
    const purchaseCosts = purchaseCostsAtPrice(price, assumptions)
    return price + purchaseCosts.total + assumptions.projectCosts - assumptions.netRefinanceAdvance - requiredCashLeft
  }

  return solveResidualPrice(assumptions, residual)
}

function solveProfitPurchasePrice(metric, assumptions) {
  const validationError = targetReturnError(metric, assumptions.targetReturn)
  if (validationError) return { price: 0, notes: validationError }
  if (assumptions.gdv <= 0) return { price: 0, notes: 'Profit target requires a positive GDV.' }

  const target = assumptions.targetReturn / 100
  const targetGdc = metric === 'profit_on_gdv'
    ? assumptions.gdv * (1 - target)
    : assumptions.gdv / (1 + target)

  return solveResidualPrice(
    assumptions,
    (price) => grossDevelopmentCost(price, assumptions) - targetGdc,
  )
}

function grossDevelopmentCost(price, assumptions) {
  return price + purchaseCostsAtPrice(price, assumptions).total + assumptions.projectCosts
}

function solveResidualPrice(assumptions, residual) {
  if (residual(0) > 0) return { price: 0, notes: 'Target cannot support fixed project costs.' }

  let low = 0
  let high = Math.max(assumptions.gdv * 2, 1)
  while (residual(high) < 0 && high < 10000000000) high *= 2

  for (let index = 0; index < 70; index += 1) {
    const midpoint = (low + high) / 2
    if (residual(midpoint) < 0) low = midpoint
    else high = midpoint
  }

  return { price: Math.round((low + high) / 2), notes: null }
}

function purchaseCostsAtPrice(price, assumptions) {
  const transactionTax = calculateTransactionTax(price, assumptions.transactionTax)
  const purchaseFinance = purchaseFinanceCost(
    assumptions.finance,
    price,
    assumptions.monthsToRefinance,
  )
  const fixed = assumptions.purchaseFees

  return {
    fixed,
    percentCost: 0,
    transactionTax,
    sdlt: transactionTax,
    purchaseFinance,
    total: fixed + transactionTax + purchaseFinance,
  }
}

function valuationForStrategy(strategy, scheme) {
  const exitMode = scheme.exitMode ?? scheme.brrr?.exitMode ?? 'Hold'

  if (strategy === 'HMO') {
    const hmo = scheme.hmo ?? {}
    const summary = calculateHmoSummary(hmo.rooms ?? [], hmo.opexPercent, hmo.yieldPercent)
    const sellGdv = hmo.valuationMethod === 'HMO Comparable'
      ? parseNumber(hmo.hmoAdoptedValue)
      : hmo.valuationMethod === 'Bricks & Mortar (£/sqft)'
        ? parseNumber(hmo.adoptedSqft) * parseNumber(hmo.internalArea)
        : summary.commercialValue
    const gdv = exitMode === 'Sell' ? sellGdv : summary.commercialValue

    return valuationForExitMode({
      gdv,
      grossAnnualIncome: summary.grossAnnualRent,
      operatingExpenses: summary.grossAnnualRent - summary.netOperatingIncome,
      netOperatingIncome: summary.netOperatingIncome,
    }, exitMode)
  }

  if (strategy === 'Commercial / Mixed-Use' || strategy === 'Commercial Conversion') {
    const summary = summarizeCommercialUnits(scheme.commercial?.units ?? [])
    return valuationForExitMode({
      gdv: summary.commercial + summary.residential,
      grossAnnualIncome: summary.grossAnnualIncome,
      operatingExpenses: summary.operatingExpenses,
      netOperatingIncome: summary.netOperatingIncome,
    }, exitMode)
  }

  const brrr = scheme.brrr ?? {}
  const gdv = brrr.valuationMethod === '£/sqft'
    ? parseNumber(brrr.adoptedSqft) * parseNumber(brrr.internalArea)
    : parseNumber(brrr.adoptedGdv)
  const grossAnnualIncome = brrr.operatingModel === 'SA'
    ? parseNumber(brrr.adoptedAdr) * 30 * (parseNumber(brrr.occupancy) / 100) * 12
    : parseNumber(brrr.adoptedMonthlyRent) * 12
  const operatingExpenses = brrr.operatingModel === 'SA'
    ? grossAnnualIncome * (parseNumber(brrr.opexPercent) / 100)
    : brrr.astOpexMode === 'annual_cost'
      ? parseNumber(brrr.astAnnualOperatingCost)
      : grossAnnualIncome * (parseNumber(brrr.astOpexPercent) / 100)

  return valuationForExitMode({
    gdv,
    grossAnnualIncome,
    operatingExpenses,
    netOperatingIncome: Math.max(grossAnnualIncome - operatingExpenses, 0),
  }, exitMode)
}

function valuationForExitMode(valuation, exitMode) {
  if (exitMode !== 'Sell') return valuation

  return {
    ...valuation,
    grossAnnualIncome: 0,
    operatingExpenses: 0,
    netOperatingIncome: 0,
  }
}

function enabledConditionalCosts(costs) {
  const values = costs.conditionalCostValues ?? {}
  const toggles = costs.conditionalCostToggles ?? {}

  return Object.entries(values).reduce((total, [name, value]) => {
    if (name === 'Sales Costs (Agent + Legals)') return total
    if (toggles[name] === false) return total
    return total + parseNumber(value)
  }, 0)
}

function disposalCostsForExitMode(costs, scheme, gdv) {
  const exitMode = scheme.exitMode ?? scheme.brrr?.exitMode ?? 'Hold'
  if (exitMode !== 'Sell') return 0

  const values = costs.disposalCostValues ?? {}
  return gdv * (parseNumber(values.estateAgentFeePercent) / 100) +
    parseNumber(values.saleLegalFee)
}

function summarizeCommercialUnits(units) {
  return units.reduce((summary, unit) => {
    const isCommercialUse = isCommercialUseType(unit.useType)
    const unitSummary = calculateCommercialUnit(unit)

    return {
      commercial: summary.commercial + (isCommercialUse ? unitSummary.value : 0),
      residential: summary.residential + (isCommercialUse ? 0 : unitSummary.value),
      income: summary.income + unitSummary.adoptedIncome,
      grossAnnualIncome: summary.grossAnnualIncome + unitSummary.grossAnnualIncome,
      operatingExpenses: summary.operatingExpenses + unitSummary.operatingExpenses,
      netOperatingIncome: summary.netOperatingIncome + unitSummary.netOperatingIncome,
    }
  }, {
    commercial: 0,
    residential: 0,
    income: 0,
    grossAnnualIncome: 0,
    operatingExpenses: 0,
    netOperatingIncome: 0,
  })
}

function purchaseFinanceCost(finance, purchasePrice, monthsToRefinance) {
  const loanAmount = purchasePrice * (parseNumber(finance.purchaseLtvPercent) / 100)
  const interest = loanAmount *
    (annualFinanceRate(finance, 'purchaseInterestRatePa', 'purchaseInterestRatePcm') / 100) *
    (monthsToRefinance / 12)
  const arrangement = loanAmount * (parseNumber(finance.purchaseArrangementFeePercent) / 100)

  return Math.round(
    interest +
    arrangement +
    parseNumber(finance.purchaseBrokerFee) +
    parseNumber(finance.purchaseValuationFee),
  )
}

function developmentFinanceCost(finance, developmentCostBase, monthsToRefinance) {
  const loanAmount = developmentCostBase * (parseNumber(finance.developmentLtcPercent) / 100)
  const interest = loanAmount *
    (annualFinanceRate(finance, 'developmentInterestRatePa', 'developmentInterestRatePcm') / 100) *
    (monthsToRefinance / 12)
  const arrangement = loanAmount * (parseNumber(finance.developmentArrangementFeePercent) / 100)

  return Math.round(
    interest +
    arrangement +
    parseNumber(finance.developmentBrokerFee) +
    parseNumber(finance.developmentValuationFee),
  )
}

function annualFinanceRate(finance, annualKey, monthlyKey) {
  if (String(finance[annualKey] ?? '').trim()) return parseNumber(finance[annualKey])

  return parseNumber(finance[monthlyKey]) * 12
}

function refinanceArrangementFeePercent(finance) {
  const value = finance.refinanceArrangementFeePercent ??
    finance.refinanceLenderFeePercent

  return parseNumber(value)
}

function sdltTypeForStrategy(strategy, scheme) {
  if (strategy === 'Commercial / Mixed-Use' || strategy === 'Commercial Conversion') return 'non_residential'

  const units = scheme.commercial?.units ?? []
  if (units.some((unit) => isCommercialUseType(unit.useType))) return 'non_residential'

  return 'residential'
}

function calculationNote(metric, targetReturn) {
  const note = `Maximum purchase price calculated from target ${formatPercent(targetReturn)} ${targetMetricLabel(metric)}.`
  if (metric === 'roce') return note

  return `${note} GDC includes the property purchase price and acquisition, development, ` +
    'and applicable disposal costs; refinance costs are excluded.'
}

function invalidMetricResult(targetMetric, assumptions) {
  return {
    maxPrice: '£0',
    targetClose: '£0',
    initialOffer: '£0',
    roce: '0%',
    cashflow: '£0/mo',
    profit: '£0',
    achievedTarget: '0%',
    formulaKey: null,
    formulaVersion: APPRAISAL_FORMULA_VERSION,
    metric: targetMetric,
    metricLabel: 'Unsupported target',
    targetReturn: formatPercent(assumptions.targetReturn),
    costBreakdown: [],
    sensitivitySignals: [],
    interpretation: 'Unsupported target metric. Select ROCE, Profit on GDV, or Profit on GDC.',
  }
}

function mockAppraisalInputs() {
  return {
    strategy: {
      currentStrategy: 'BRRR / Flip',
      targetMetric: 'ROCE',
      targetReturn: '20',
    },
    scheme: {
      brrr: {
        valuationMethod: 'Comparable',
        adoptedGdv: '285,000',
        operatingModel: 'AST',
        adoptedMonthlyRent: '1,450',
      },
    },
    costs: {
      baseCostValues: {
        'Refurb / Build Cost': '85,000',
        'Contingency (% of refurb/build)': '10',
        'Professional Fees': '4,500',
        'Legal Costs': '2,500',
        'Survey Costs': '950',
      },
      conditionalCostValues: {},
      conditionalCostToggles: {},
    },
    holding: {
      monthlyHoldingCosts: '450',
      monthsToRefinance: '6',
    },
    finance: {
      purchaseInterestRatePa: '10.2',
      purchaseLtvPercent: '75',
      purchaseBrokerFee: '495',
      purchaseArrangementFeePercent: '2',
      purchaseValuationFee: '450',
      developmentInterestRatePa: '11.4',
      developmentLtcPercent: '100',
      developmentBrokerFee: '495',
      developmentArrangementFeePercent: '1.5',
      developmentValuationFee: '0',
      refinanceInterestRatePa: '5.5',
      refinanceLtvPercent: '75',
      refinanceBrokerFee: '495',
      refinanceArrangementFeePercent: '0.7',
      refinanceValuationFee: '498.75',
    },
  }
}
